import type { Member } from '../models/member/member'
import useGet from './api/useGet'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import usePost from './api/usePost'
import { ApiRequestStatus } from '../models/api/api-request-status'

export const useComments = (params: () => { dialogId?: string; dialogSlideId?: string }) => {
  const { data, load, status, reload } = useGet<Comment[]>(`comment`)

  const query = computed(() => {
    const { dialogId, dialogSlideId } = params()
    if (dialogId && dialogSlideId) {
      return {
        dialogId: dialogId,
        dialogSlideId: dialogSlideId,
      }
    }
    return undefined
  })

  onMounted(() => {
    load({ query: query.value })
  })

  watch(query, () => {
    load({ query: query.value })
  })

  const { mutate: create, status: createStatus } = usePost<CreateableComment>(`comment`)

  const creatableComment = reactive<Partial<CreateableComment>>({
    isShared: false,
    isConversationNote: false,
  })

  const saveComment = async () => {
    if (creatableComment && creatableComment.text) {
      await create({ ...creatableComment, ...query.value })
    }
    if (createStatus.value == ApiRequestStatus.Success) {
      cancelCreateComment()
      reload({ query: query.value })
    }
  }

  const showForm = ref(false)

  const createComment = (isConversationNote = false) => {
    creatableComment.isConversationNote = isConversationNote
    if (isConversationNote) {
      creatableComment.isShared = true
    }
    showForm.value = true
  }

  const cancelCreateComment = () => {
    showForm.value = false
    creatableComment.text = undefined
    creatableComment.isShared = false
    creatableComment.isConversationNote = false
  }

  const groupCommentsByAuthor = (comments: Comment[]): Comment[][] => {
    if (comments.length === 0) {
      return []
    }

    const groups: Comment[][] = []
    let previousAuthorId: string | undefined = undefined
    let currentGroup: Comment[] = []

    for (const comment of comments) {
      if (previousAuthorId && comment.author.id !== previousAuthorId) {
        groups.push(currentGroup)
        currentGroup = []
      }
      previousAuthorId = comment.author.id
      currentGroup.push(comment)
    }
    groups.push(currentGroup)

    return groups
  }

  const commentGroups = computed(() => {
    const comments = data.value?.filter((comment) => !comment.isConversationNote) ?? []
    return groupCommentsByAuthor(comments)
  })

  const conversationNoteGroups = computed(() => {
    const notes = data.value?.filter((comment) => comment.isConversationNote) ?? []
    return groupCommentsByAuthor(notes)
  })

  const updateComment = (comment: Comment) => {
    data.value = data.value?.map((c) => (c.id === comment.id ? comment : c))
  }

  const deleteComment = (commentId: string) => {
    data.value = data.value?.filter((c) => c.id !== commentId)
  }

  return {
    commentGroups,
    conversationNoteGroups,
    status,
    saveComment,
    createComment,
    cancelCreateComment,
    creatableComment,
    showForm,
    updateComment,
    deleteComment,
  }
}

export interface Comment {
  readonly id: string
  readonly text: JSON
  readonly author: Member
  readonly createdAt: string
  readonly replies?: ReadonlyArray<Comment>
  readonly isShared: boolean
  readonly isConversationNote: boolean
}

export interface CreateableComment {
  text: JSON
  dialogId?: string
  dialogSlideId?: string
  replyToId?: string
  isShared?: boolean
  isConversationNote?: boolean
}
